import Cookies from 'js-cookie';
import {makePatchRefundFinishRequest} from 'main/factories/usecases';
import {HomeEnum, OperationStateTypesEnum, RefundEnum} from 'presentation/constants';
import {translations} from 'presentation/translations-new';
import {useState} from 'react';
import {useNavigate} from 'react-router-dom';

import {useSendAdditionalDocsDataStore} from '../pages/SendAdditionalDocs/store/useSendAdditionalDocsDataStore';
import {useStepSendDocsStore} from '../pages/SendAdditionalDocs/store/useStepSendDocsStore';
import {FormStepEnum} from '../pages/SendAdditionalDocs/types';
import {useResetRefundFlow} from './useResetRefundFlow';

type UseSendAdditionalDocsParams = {
	refundTermsReturnTo?: string;
};

const {stepsLabels, stepsTexts} = translations['pt-br'].privateFlow.refund.additionalDocuments;

export function useSendAdditionalDocs({refundTermsReturnTo}: UseSendAdditionalDocsParams = {}) {
	const navigate = useNavigate();
	const {resetRefundFlow} = useResetRefundFlow();
	const formStep = useStepSendDocsStore(state => state.step);
	const nextFormStep = useStepSendDocsStore(state => state.nextStep);
	const previousFormStep = useStepSendDocsStore(state => state.previousStep);
	const [exitFlowModalState, setExitFlowModalState] = useState(false);
	const [concludeRequestSendAdditionalDocsState, setConcludeRequestSendAdditionalDocsState] =
		useState(OperationStateTypesEnum.NONE);
	const sendAdditionalDocsData = useSendAdditionalDocsDataStore(state => state.data);
	const [conclusionModalState, setConclusionModalState] = useState<boolean>(false);
	const protocolNumber = sendAdditionalDocsData?.protocol;

	const blockBackAction = formStep === FormStepEnum.SENDING_FILES;

	const handleOpenExitModal = () => {
		setExitFlowModalState(true);
	};

	const handleClose = () => {
		setExitFlowModalState(true);
		Cookies.remove('uploadFilesTerm');
	};

	const handleBack = () => {
		if (formStep === FormStepEnum.BENEFICIARY_DATA) {
			if (refundTermsReturnTo) return setExitFlowModalState(true);
			return navigate(RefundEnum.TERMS_ADDITIONAL_DOCS);
		}

		if (formStep === FormStepEnum.SEARCH_PROTOCOL) {
			return setExitFlowModalState(true);
		}

		return previousFormStep();
	};

	const handleNextStep = async () => {
		if (formStep === FormStepEnum.CONFERENCE) {
			try {
				setConcludeRequestSendAdditionalDocsState(OperationStateTypesEnum.SUBMITTING);
				await makePatchRefundFinishRequest(sendAdditionalDocsData.protocolCode).patch();
				setConclusionModalState(true);

				Cookies.remove('uploadFilesTerm');
				setConcludeRequestSendAdditionalDocsState(OperationStateTypesEnum.DONE);
				return;
			} catch (error) {
				setConcludeRequestSendAdditionalDocsState(OperationStateTypesEnum.ERROR);
				return;
			}
		}

		nextFormStep();
	};

	const handleGoToHome = () => {
		navigate(HomeEnum.HOME_PAGE);
	};

	const handleAbandonFlowModalAction = () => {
		resetRefundFlow();
		setExitFlowModalState(false);
		navigate(refundTermsReturnTo ?? RefundEnum.REFUND);
	};

	const formStepsLabels = [
		stepsLabels.beneficiaryData,
		stepsLabels.searchProtocol,
		stepsLabels.sendDocuments,
		stepsLabels.conference,
	];

	const formStepsTexts = {
		[FormStepEnum.BENEFICIARY_DATA]: stepsTexts.beneficiaryData,
		[FormStepEnum.SEARCH_PROTOCOL]: stepsTexts.searchProtocol,
		[FormStepEnum.SENDING_FILES]: stepsTexts.sendDocuments,
		[FormStepEnum.CONFERENCE]: stepsTexts.conference,
	};

	const IS_LOADING =
		concludeRequestSendAdditionalDocsState === OperationStateTypesEnum.SUBMITTING;
	const IS_ERROR = concludeRequestSendAdditionalDocsState === OperationStateTypesEnum.ERROR;

	return {
		handleOpenExitModal,
		exitFlowModalState,
		setExitFlowModalState,
		handleBack,
		handleNextStep,
		setConcludeRequestSendAdditionalDocsState,
		IS_LOADING,
		IS_ERROR,
		formStepsTexts,
		formStepsLabels,
		handleClose,
		handleAbandonFlowModalAction,
		blockBackAction,
		handleGoToHome,
		protocol: protocolNumber,
		setConclusionModalState,
		conclusionModalState,
	};
}
